import * as THREE from 'three';
import { applySceneLighting } from './SceneLighting';
/**
 * Outils partages par toute la geometrie procedurale de la scene.
 *
 * Le decor est baked et rendu en MeshBasicMaterial : aucune lumiere Three.js
 * n'eclaire quoi que ce soit, donc aucun relief n'apparait de lui-meme et un
 * pave reste une silhouette plate. Le relief est donc PEINT — chaque face
 * recoit sa propre couleur de sommets, claire dessus et sombre sur les cotes,
 * ce qui imite une lumiere de plafond. Les halos de SceneLighting viennent
 * ensuite par-dessus, mais ils ne remplacent pas ce relief de base : ils ne
 * couvrent qu'une partie de la piece.
 *
 * Extrait de GamerSetup, ou tout cela vivait, quand le bureau, la tasse et
 * les documents ont eu besoin des memes outils.
 */
/** Renderer.outputEncoding vaut sRGBEncoding : il faut convertir a l'ecriture
 * pour que les valeurs hexadecimales ecrites ici soient celles qu'on voit. */
export const srgb = (hex) => new THREE.Color(hex).convertSRGBToLinear();
// BoxGeometry range ses 24 sommets par face, 4 par face, dans cet ordre.
const FACE = { px: 0, nx: 4, py: 8, ny: 12, pz: 16, nz: 20 };
export const tintedBox = (w, h, d, tint) => {
    const geo = new THREE.BoxGeometry(w, h, d);
    const colors = new Float32Array(24 * 3);
    const paint = (start, hex) => {
        const c = srgb(hex);
        for (let i = 0; i < 4; i++) {
            colors[(start + i) * 3] = c.r;
            colors[(start + i) * 3 + 1] = c.g;
            colors[(start + i) * 3 + 2] = c.b;
        }
    };
    paint(FACE.px, tint.side);
    paint(FACE.nx, tint.side);
    paint(FACE.py, tint.top);
    paint(FACE.ny, tint.bottom === undefined ? tint.side : tint.bottom);
    paint(FACE.pz, tint.front === undefined ? tint.side : tint.front);
    paint(FACE.nz, tint.side);
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
};
/**
 * Peint un degrade vertical sur une geometrie quelconque, du bas vers le
 * haut de sa propre boite englobante. Sert aux volumes tournes ou deformes,
 * ou il n'y a pas de faces franches a teinter une par une.
 */
export const paintVerticalGradient = (geo, lowHex, highHex, curve = 0.8) => {
    geo.computeBoundingBox();
    const box = geo.boundingBox;
    const y0 = box.min.y;
    const span = Math.max(1e-6, box.max.y - y0);
    const pos = geo.attributes.position;
    const low = srgb(lowHex);
    const high = srgb(highHex);
    const c = new THREE.Color();
    const colors = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
        const t = Math.pow((pos.getY(i) - y0) / span, curve);
        c.copy(low).lerp(high, t);
        colors[i * 3] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
};
/** Teinte une geometrie d'une seule couleur, pour pouvoir la fusionner avec
 * les autres : le fusionneur exige l'attribut color partout. */
export const paintFlat = (geo, hex) => {
    const pos = geo.attributes.position;
    const c = srgb(hex);
    const colors = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
        colors[i * 3] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
};
/** Materiau commun aux volumes proceduraux : relief en couleurs de sommets,
 * plus les halos de scene. */
export const tintedMaterial = () => {
    const material = new THREE.MeshBasicMaterial({ vertexColors: true });
    applySceneLighting(material);
    return material;
};
/**
 * Fusionne les morceaux en un seul maillage. Le clavier compte pres de 90
 * capuchons, les piles de documents une centaine de feuilles : autant de Mesh
 * separes, c'est autant d'appels de rendu par image pour des volumes
 * minuscules.
 *
 * Fusion ecrite ici plutot qu'importee de BufferGeometryUtils : ce module des
 * examples de three est un module ES, que require() ne peut pas charger. En
 * dependre rendait la geometrie inverifiable hors navigateur, alors que
 * recoller des volumes indexes tient en quelques lignes.
 *
 * position, normal et color sont conserves. Les normales sont necessaires
 * depuis que les halos de SceneLighting s'en servent pour savoir quelles
 * faces regardent la lumiere ; les UV, elles, ne servent a rien sans texture.
 */
export const mergedGeometry = (parts) => {
    let vTotal = 0;
    let iTotal = 0;
    parts.forEach((p) => {
        vTotal += p.attributes.position.count;
        iTotal += p.index ? p.index.count : 0;
    });
    const position = new Float32Array(vTotal * 3);
    const normal = new Float32Array(vTotal * 3);
    const color = new Float32Array(vTotal * 3);
    const index = new Uint32Array(iTotal);
    let vAt = 0;
    let iAt = 0;
    parts.forEach((p) => {
        if (!p.attributes.normal)
            p.computeVertexNormals();
        const n = p.attributes.position.count;
        const pa = p.attributes.position.array;
        const na = p.attributes.normal.array;
        const ca = p.attributes.color.array;
        for (let k = 0; k < n * 3; k++) {
            position[vAt * 3 + k] = pa[k];
            normal[vAt * 3 + k] = na[k];
            color[vAt * 3 + k] = ca[k];
        }
        if (p.index) {
            const ia = p.index.array;
            // Les indices de chaque morceau sont decales du nombre de sommets
            // deja ecrits, sinon toutes les faces pointeraient sur le premier.
            for (let k = 0; k < p.index.count; k++)
                index[iAt + k] = ia[k] + vAt;
            iAt += p.index.count;
        }
        vAt += n;
        p.dispose();
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(position, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(color, 3));
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    return geo;
};
export const mergedMesh = (parts) => new THREE.Mesh(mergedGeometry(parts), tintedMaterial());
/**
 * Tache d'ombre posee sous un objet.
 *
 * Le decor tenait ses ombres de la texture bakee. Des qu'un meuble est
 * remplace par de la geometrie, ces ombres disparaissent avec lui et tout ce
 * qui etait pose dessus semble flotter. Celles-ci les remplacent.
 *
 * Le degrade est construit en DataTexture et non sur un canvas : c'est du
 * calcul pur, donc la geometrie reste verifiable hors navigateur.
 */
let shadowTexture = null;
const getShadowTexture = () => {
    if (shadowTexture)
        return shadowTexture;
    const N = 64;
    const data = new Uint8Array(N * N * 4);
    for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
            const dx = ((x + 0.5) / N) * 2 - 1;
            const dy = ((y + 0.5) / N) * 2 - 1;
            const r = Math.min(1, Math.sqrt(dx * dx + dy * dy));
            // Puissance > 1 : une rampe lineaire laisse un disque au bord trop
            // net, qui se voit comme une auréole.
            const a = Math.round(255 * Math.pow(1 - r, 1.7));
            const i = (y * N + x) * 4;
            // alphaMap lit le canal vert ; les trois canaux sont remplis pour
            // que la texture reste lisible quel que soit l'usage.
            data[i] = a;
            data[i + 1] = a;
            data[i + 2] = a;
            data[i + 3] = 255;
        }
    }
    shadowTexture = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
    shadowTexture.needsUpdate = true;
    return shadowTexture;
};
export const contactShadow = (w, d, opacity = 0.45) => {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity,
        alphaMap: getShadowTexture(),
        // Sans ca, la tache masquerait dans le tampon de profondeur ce qui
        // passe derriere elle.
        depthWrite: false,
    }));
    mesh.rotation.x = -Math.PI / 2;
    return mesh;
};
